'use client';

import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import { Zap, Users, Target } from 'lucide-react';
import { isFinnishPage } from '../../../lib/blogPaths';

// Same navy/slate scale as Software Dev – unified family, distinct steps
const UIUX_COLLABORATION_COLORS = [
  { main: '#0f172a', badgeBg: '#f1f5f9', badgeText: '#334155' },
  { main: '#1e293b', badgeBg: '#e2e8f0', badgeText: '#334155' },
  { main: '#334155', badgeBg: '#cbd5e1', badgeText: '#1e293b' },
];

const MODELS_EN = [
  { icon: Zap, title: 'Design sprint', tagline: 'Quick overview and solution proposals', description: 'Using a design sprint, it is possible to gain a quick overview of the status of the product or service being developed before starting the actual design and development work. A design sprint can also be applied to rapidly generate solution proposals.' },
  { icon: Users, title: 'Continuous design', tagline: 'Designer as part of your team', description: "Our designer becomes part of your development team for an indefinite period or for a predefined duration. The designer regularly participates in the team's meetings, ensuring a cohesive and user-centered system." },
  { icon: Target, title: 'Targeted expertise', tagline: 'Focused support where you need it', description: 'For clients who have a product or service that requires assistance in a more specific area, such as user research, user interface design, or testing.' },
];

const MODELS_FI = [
  { icon: Zap, title: 'Design sprint', tagline: 'Nopea kokonaiskuva ja ratkaisuehdotukset', description: 'Design sprintin avulla on mahdollista saada nopea kokonaiskuva kehitettävän tuotteen tai palvelun tilasta ennen varsinaisen suunnittelu- ja kehitystyön aloittamista. Design sprintiä voidaan soveltaa myös ratkaisuehdotuksien nopeaan tuottamiseen.' },
  { icon: Users, title: 'Jatkuva suunnittelu', tagline: 'Suunnittelija osana tiimiäsi', description: 'Suunnittelijamme liittyy osaksi kehitystiimiänne toistaiseksi tai ennalta määritellyksi ajaksi. Suunnittelija osallistuu tiimin yhteisiin tapaamisiin säännöllisesti ja varmistaa näin yhtenäisen sekä käyttäjän keskiöön asettavan järjestelmän.' },
  { icon: Target, title: 'Täsmäosaaminen', tagline: 'Kohdennettu tuki tarvitsemaasi kohtaan', description: 'Asiakkaille, joilla on tuote tai palvelu, jonka kehittämiseen tarvitaan apua tarkemmin määritellyltä osa-alueelta, kuten käyttäjätutkimuksesta, käyttöliittymäsuunnittelusta tai testauksesta.' },
];

export function UiUxCollaborationSection() {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isFinnish = isFinnishPage(router?.pathname);
  const models = isFinnish ? MODELS_FI : MODELS_EN;
  const heading = isFinnish ? 'Erilaiset yhteistyömallit' : 'Collaboration models';
  const subtitle = isFinnish ? 'Miten työskentelemme kanssanne UX- ja UI-suunnittelussa—sprinteistä jatkuvaan kumppanuuteen.' : 'How we work with you on UX and UI design—from sprints to ongoing partnership.';

  return (
    <section ref={ref} className="section-rhythm bg-gradient-to-b from-[#f8fafc] to-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="heading-2 mb-4">{heading}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {models.map((item, index) => {
            const Icon = item.icon;
            const color = UIUX_COLLABORATION_COLORS[index];
            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 28 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative flex flex-col p-6 lg:p-8 rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-[0_16px_48px_-12px_rgba(0,0,0,0.12)] hover:border-gray-300 transition-all duration-300"
              >
                <div className="flex flex-col">
                  <span
                    className="flex items-center justify-center w-12 h-12 rounded-xl shrink-0 shadow-sm mb-4 text-white"
                    style={{ backgroundColor: color.main }}
                    aria-hidden
                  >
                    <Icon className="w-6 h-6" />
                  </span>
                  <span
                    className="inline-block w-fit px-2.5 py-1 rounded-md text-xs font-medium mb-3"
                    style={{ backgroundColor: color.badgeBg, color: color.badgeText }}
                  >
                    {item.tagline}
                  </span>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-[15px] text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
